import { FunctionComponent, useMemo, useState } from 'react'
import { Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { Search, ChevronDown, ChevronRight, ArrowRight } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import colors from '../../colors'
import { HELP_SECTIONS, HelpTopic, SUPPORT_EMAIL } from './helpTopics'

/**
 * Self-service help: questions phrased the way a brewery asks them, answered
 * with the exact clicks and whatever precondition will otherwise bite them.
 *
 * Answers open in place. Sending someone to an external help site mid-task is
 * the thing that breaks a self-service loop, so everything here is answered
 * without leaving the page - and each answer offers to take them there.
 *
 * Content lives in helpTopics.ts and is meant to be edited freely.
 *
 * Plain elements rather than MUI ones: @material-ui/core v5-beta (emotion)
 * silently overrides @material-ui/styles v4 (JSS) classes.
 */
const Help: FunctionComponent = () => {
  const styles = useStyles()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState<string[]>([])

  const toggle = (question: string) =>
    setOpen((prev) =>
      prev.includes(question) ? prev.filter((q) => q !== question) : [...prev, question]
    )

  // Matches the note and the click path too, not just the question - someone
  // searching "stripe" should find "How do I enable a membership?", whose
  // answer is really about Stripe.
  const sections = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return HELP_SECTIONS

    const matches = (topic: HelpTopic) =>
      [topic.question, topic.note ?? '', topic.path.join(' ')]
        .join(' ')
        .toLowerCase()
        .includes(needle)

    return HELP_SECTIONS
      .map((section) => ({ ...section, topics: section.topics.filter(matches) }))
      .filter((section) => section.topics.length > 0)
  }, [query])

  const resultCount = sections.reduce((n, s) => n + s.topics.length, 0)
  const searching = query.trim().length > 0

  return (
    <Box className={styles.page}>
      <Box className={styles.container}>
        <h1 className={styles.heading}>How can we help?</h1>

        <div className={styles.searchWrap}>
          <Search size={18} className={styles.searchIcon} />
          <input
            className={styles.search}
            type="search"
            placeholder="Search help…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search help"
          />
        </div>

        {searching && (
          <div className={styles.resultCount}>
            {resultCount === 0
              ? 'No answers matched. Try a different word, or email us below.'
              : `${resultCount} ${resultCount === 1 ? 'answer' : 'answers'}`}
          </div>
        )}

        {sections.map((section) => (
          <div key={section.title} className={styles.section}>
            <h2 className={styles.sectionTitle}>{section.title}</h2>

            {section.topics.map((topic) => {
              // While searching, show answers open - the user has already told
              // us what they want and should not have to click again.
              const expanded = searching || open.includes(topic.question)

              return (
                <div key={topic.question} className={styles.topic}>
                  <button
                    type="button"
                    className={styles.question}
                    aria-expanded={expanded}
                    onClick={() => toggle(topic.question)}
                  >
                    {expanded
                      ? <ChevronDown size={16} className={styles.chevron} />
                      : <ChevronRight size={16} className={styles.chevron} />}
                    <span>{topic.question}</span>
                  </button>

                  {expanded && (
                    <div className={styles.answer}>
                      <div className={styles.path}>
                        {topic.path.map((step, i) => (
                          <span key={i}>
                            <span className={styles.step}>{step}</span>
                            {i < topic.path.length - 1 && (
                              <span className={styles.arrow}>→</span>
                            )}
                          </span>
                        ))}
                      </div>

                      {topic.note && <div className={styles.note}>{topic.note}</div>}

                      {topic.to && (
                        <button
                          type="button"
                          className={styles.takeMeThere}
                          onClick={() => navigate(topic.to as string)}
                        >
                          Take me there
                          <ArrowRight size={14} style={{ marginLeft: 6 }} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ))}

        <div className={styles.footer}>
          <span className={styles.footerLead}>Still stuck?</span>{' '}
          <a className={styles.footerLink} href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          <div className={styles.footerNote}>
            Tell us what you were trying to do and we will walk you through it.
          </div>
        </div>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles({
  page: {
    background: colors.GREY_7,
    minHeight: '100vh',
    padding: '48px 24px',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
  },
  heading: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
    margin: '0 0 20px',
  },
  searchWrap: {
    position: 'relative' as const,
    marginBottom: 8,
  },
  searchIcon: {
    position: 'absolute' as const,
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.GREY_3,
    pointerEvents: 'none' as const,
  },
  search: {
    width: '100%',
    boxSizing: 'border-box' as const,
    padding: '14px 16px 14px 42px',
    fontSize: 15,
    borderRadius: 10,
    border: `1px solid ${colors.GREY_4}`,
    background: colors.WHITE,
    outline: 'none',
    '&:focus': {
      borderColor: colors.BRIGHT_BLUE,
      boxShadow: `0 0 0 3px ${colors.PALE_BLUE}`,
    },
  },
  resultCount: {
    fontSize: 12,
    color: colors.GREY_3,
    marginBottom: 16,
  },
  section: {
    marginTop: 32,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase' as const,
    color: colors.GREY_3,
    margin: '0 0 8px',
  },
  topic: {
    background: colors.WHITE,
    border: `1px solid ${colors.GREY_5}`,
    borderRadius: 10,
    marginBottom: 8,
    overflow: 'hidden',
  },
  question: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'left' as const,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '14px 16px',
    fontSize: 15,
    fontWeight: 600,
    color: colors.STITCH_BLACK,
    fontFamily: 'inherit',
  },
  chevron: {
    color: colors.GREY_3,
    flexShrink: 0,
  },
  answer: {
    padding: '0 16px 16px 42px',
  },
  path: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: 4,
    marginBottom: 10,
  },
  step: {
    display: 'inline-block',
    background: colors.PALE_BLUE,
    color: colors.STITCH_BLUE,
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 6,
    padding: '4px 9px',
  },
  arrow: {
    color: colors.GREY_3,
    margin: '0 5px',
    fontSize: 13,
  },
  note: {
    fontSize: 13,
    lineHeight: 1.55,
    color: colors.GREY_2,
    marginBottom: 12,
  },
  takeMeThere: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: colors.BRIGHT_BLUE,
  },
  footer: {
    marginTop: 40,
    paddingTop: 20,
    borderTop: `1px solid ${colors.GREY_5}`,
  },
  footerLead: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.GREY_1,
  },
  footerLink: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.BRIGHT_BLUE,
    textDecoration: 'none',
  },
  footerNote: {
    fontSize: 12,
    color: colors.GREY_3,
    marginTop: 6,
  },
})

export default Help
